import { useState, useMemo } from 'react'
import ResultsPanel from '@/components/ResultsPanel'

interface HeatPumpInputs {
  capacityTons: number
  currentHSPF: number
  newHSPF: number
  annualHeatingHours: number
  electricityRate: number
  installationCost: number
}

const DEFAULT: HeatPumpInputs = {
  capacityTons: 3,
  currentHSPF: 7.7,
  newHSPF: 10,
  annualHeatingHours: 1200,
  electricityRate: 0.15,
  installationCost: 6000,
}

interface SliderFieldProps {
  id: string
  label: string
  value: number
  min: number
  max: number
  step: number
  unit: string
  prefix?: string
  decimals?: number
  onChange: (v: number) => void
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  unit,
  prefix = '',
  decimals = 0,
  onChange,
}: SliderFieldProps) {
  const pct = ((value - min) / (max - min)) * 100
  const display = decimals > 0 ? value.toFixed(decimals) : value

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-medium text-slate-300">
          {label}
        </label>
        <span
          aria-live="polite"
          aria-label={`${label}: ${prefix}${display}${unit}`}
          className="text-sm font-bold tabular-nums px-2.5 py-0.5 rounded-md bg-orange-500/15 text-orange-300"
        >
          {prefix}{display}{unit}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-orange-500"
        style={{
          backgroundImage: `linear-gradient(to right, #f97316 ${pct}%, transparent ${pct}%)`,
        }}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${prefix}${display}${unit}`}
      />
      <div className="flex justify-between text-xs text-slate-600">
        <span>{prefix}{decimals > 0 ? min.toFixed(decimals) : min}{unit}</span>
        <span>{prefix}{decimals > 0 ? max.toFixed(decimals) : max}{unit}</span>
      </div>
    </div>
  )
}

// HSPF formula (heating):
// kW drawn = (capacity BTU/hr) / (HSPF * 1000)
// Annual heating cost = kW * hours * rate
function computeHeatPumpSavings(inputs: HeatPumpInputs) {
  const btuh = inputs.capacityTons * 12000
  const oldKW = btuh / (inputs.currentHSPF * 1000)
  const newKW = btuh / (inputs.newHSPF * 1000)
  const oldAnnualCost = oldKW * inputs.annualHeatingHours * inputs.electricityRate
  const newAnnualCost = newKW * inputs.annualHeatingHours * inputs.electricityRate
  const annualSavings = oldAnnualCost - newAnnualCost
  const tenYearSavings = annualSavings * 10
  const annualKWhSaved = (oldKW - newKW) * inputs.annualHeatingHours
  const co2Saved = annualKWhSaved * 1.37
  const paybackYears =
    inputs.installationCost > 0 && annualSavings > 0
      ? inputs.installationCost / annualSavings
      : null

  return {
    oldAnnualCost,
    newAnnualCost,
    annualSavings,
    tenYearSavings,
    co2Saved,
    paybackYears,
    annualKWhSaved,
  }
}

export default function HeatPumpCalculator() {
  const [inputs, setInputs] = useState<HeatPumpInputs>(DEFAULT)
  const set = (key: keyof HeatPumpInputs) => (v: number) =>
    setInputs((prev) => ({ ...prev, [key]: v }))

  const results = useMemo(() => computeHeatPumpSavings(inputs), [inputs])

  return (
    <div className="p-6 lg:p-8">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Inputs Column */}
        <div>
          <h3 className="text-base font-semibold text-slate-200 mb-6 flex items-center gap-2">
            <span
              aria-hidden="true"
              className="w-6 h-6 rounded bg-orange-600/20 flex items-center justify-center text-orange-400 text-xs"
            >
              🔥
            </span>
            Heat Pump HSPF Calculator — System Parameters
          </h3>

          <div className="space-y-6" aria-label="Heat pump HSPF calculator inputs">
            <SliderField
              id="hp-capacity"
              label="Capacity (Tons)"
              value={inputs.capacityTons}
              min={1.5}
              max={5}
              step={0.5}
              unit=" tons"
              onChange={set('capacityTons')}
              decimals={1}
            />
            <SliderField
              id="hp-current-hspf"
              label="Current HSPF Rating"
              value={inputs.currentHSPF}
              min={6}
              max={10}
              step={0.1}
              unit=""
              onChange={set('currentHSPF')}
              decimals={1}
            />
            <SliderField
              id="hp-new-hspf"
              label="New HSPF Rating"
              value={inputs.newHSPF}
              min={8.2}
              max={14}
              step={0.1}
              unit=""
              onChange={set('newHSPF')}
              decimals={1}
            />
            <SliderField
              id="hp-heating-hours"
              label="Annual Heating Hours"
              value={inputs.annualHeatingHours}
              min={500}
              max={3000}
              step={50}
              unit=" hrs"
              onChange={set('annualHeatingHours')}
              decimals={0}
            />
            <SliderField
              id="hp-electricity-rate"
              label="Electricity Rate"
              value={inputs.electricityRate}
              min={0.08}
              max={0.45}
              step={0.01}
              unit="/kWh"
              prefix="$"
              decimals={2}
              onChange={set('electricityRate')}
            />

            {/* Installation Cost */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="hp-install-cost" className="text-sm font-medium text-slate-300">
                  Installation Cost{' '}
                  <span className="text-slate-600 font-normal">(optional)</span>
                </label>
                <span className="text-sm font-bold text-orange-300 tabular-nums bg-orange-500/15 px-2.5 py-0.5 rounded-md">
                  ${inputs.installationCost.toLocaleString()}
                </span>
              </div>
              <input
                id="hp-install-cost"
                type="range"
                min={0}
                max={20000}
                step={100}
                value={inputs.installationCost}
                onChange={(e) => set('installationCost')(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-orange-500"
                style={{
                  backgroundImage: `linear-gradient(to right, #f97316 ${(inputs.installationCost / 20000) * 100}%, transparent ${(inputs.installationCost / 20000) * 100}%)`,
                }}
                aria-valuemin={0}
                aria-valuemax={20000}
                aria-valuenow={inputs.installationCost}
                aria-valuetext={`$${inputs.installationCost.toLocaleString()}`}
              />
              <div className="flex justify-between text-xs text-slate-600">
                <span>$0</span>
                <span>$20,000</span>
              </div>
            </div>
          </div>
        </div>

        {/* Results Column */}
        <ResultsPanel
          oldAnnualCost={results.oldAnnualCost}
          newAnnualCost={results.newAnnualCost}
          annualSavings={results.annualSavings}
          tenYearSavings={results.tenYearSavings}
          co2Saved={results.co2Saved}
          paybackYears={results.paybackYears}
          annualKWhSaved={results.annualKWhSaved}
          accentColor="orange"
          oldLabel={`Current HSPF ${inputs.currentHSPF.toFixed(1)}`}
          newLabel={`New HSPF ${inputs.newHSPF.toFixed(1)}`}
        />
      </div>
    </div>
  )
}
